/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerHubPage.tsx
 * PURPOSE: Màn Tổng quan Quản lý — SC-010-HUB-BM (Release v0 WP-2)
 *          + 2026-08-17: ISS-MGR-11 (§27) — widget [4] MỚI "Tổng giờ làm"
 *          (tuần/tháng, per nhân viên), gọi GET .../analytics/manager/hours-summary
 *          (DD-0093, endpoint đã build sẵn commit b896369f1) — UI widget trước
 *          đó CHƯA tồn tại, tái dùng đúng pattern bảng+card của widget [3]
 *          + 2026-08-17: DD-0103 (ISS-PILOT-17 "thống kê" half) — thêm cột/badge
 *          "Cảnh báo" (days_over_ot_cap/insufficient_rest_days) vào widget [4],
 *          tái dùng pattern covBad/renderAnomaly của widget [3]
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager Hub (Tổng quan) — Vietnamese F&B Workforce Management
 * - Impact: UX Critical — màn làm việc đầu ngày của Quản lý chi nhánh / Trưởng ca
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: React + TanStack Query (useManagerHub hooks) — mọi số liệu từ API
 *   thật (manager_hub.py), loading skeleton + empty state + dialog [N] + toast
 * - Dependencies: React Router, useAuthStore, useManagerHub, NQH design tokens
 *
 * @cross-references
 * - Mockup chuẩn: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/hub.html
 * - Biến thể Trưởng ca (ẩn [6]): .../manager/hub-shift.html
 * - Hooks: frontend/src/hooks/useManagerHub.ts
 * - Backend: backend/app/api/v2/endpoints/manager_hub.py
 * ===============================================================================
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../../store/auth";
import {
  useManagerCoverageToday,
  useManagerHoursSummary,
  useManagerWeekSummary,
  useRemindMissing,
} from "../../hooks/useManagerHub";
import type {
  CoverageAreaItem,
  EmployeeHoursItem,
  MissingStaffItem,
} from "../../services/api/managerApi";
import styles from "./ManagerHubPage.module.css";

// apiV2 (services/api/config.ts) now surfaces the real backend message on
// error.message (fixed 2026-08-03). Previously ignored `error` entirely.
const actionErrorMessage = (error: unknown, fallback: string): string =>
  (error as { message?: string } | undefined)?.message || fallback;

// ── Inline SVG icons (Heroicons — copy nguyên path từ mockup, 0 emoji) ──────

const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={15} height={15} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const WarnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, "0");

/** dd/mm/yyyy từ Date (ngày THẬT client) */
const formatDateVi = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

/** dd/mm từ ISO date string (yyyy-mm-dd) của API */
const formatDayMonth = (iso: string) => {
  const [, mm, dd] = iso.split("-");
  return `${dd}/${mm}`;
};

const COVERAGE_TOOLTIP =
  "Thống kê theo ca: số ca đã có chấm công (clock-in) trên tổng số ca theo lịch hôm nay của khu vực.";

/** Dialog [N] target: khu vực cụ thể hoặc "all" (mọi khu vực thiếu phủ) */
interface RemindTarget {
  workAreaId: string | null;
  areaName: string | null;
  staff: Array<MissingStaffItem & { areaName?: string }>;
}

const ManagerHubPage: React.FC = () => {
  const { userRole, tenantKey } = useAuthStore();
  const isBranchManager = (userRole?.toUpperCase() || "") === "BRANCH_MANAGER";

  const coverage = useManagerCoverageToday();
  const weekSummary = useManagerWeekSummary();
  const remindMutation = useRemindMissing();

  // Widget MỚI [4] Tổng giờ làm (ISS-MGR-11 / DD-0093) — mặc định "Tuần"
  // (DD-0093 §4.2 không chốt default, để @ba đổi sau nếu cần).
  const [hoursPeriod, setHoursPeriod] = useState<"week" | "month">("week");
  const hoursSummary = useManagerHoursSummary(hoursPeriod);

  const [remindTarget, setRemindTarget] = useState<RemindTarget | null>(null);
  const [remindError, setRemindError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const coverageData = coverage.data;
  const weekData = weekSummary.data;
  const hoursData = hoursSummary.data;
  const branchLabel =
    weekData?.branch_code || weekData?.branch_name || coverageData?.branch_code ||
    coverageData?.branch_name || tenantKey || "";

  // ── Dialog [N] handlers ──
  const openRemindArea = (area: CoverageAreaItem) => {
    setRemindError(null);
    setRemindTarget({
      workAreaId: area.work_area_id,
      areaName: area.work_area_name,
      staff: area.missing_staff,
    });
  };

  const openRemindAll = () => {
    if (!coverageData) return;
    setRemindError(null);
    const staff = coverageData.areas
      .filter((area) => area.missing_clock_in > 0)
      .flatMap((area) =>
        area.missing_staff.map((s) => ({ ...s, areaName: area.work_area_name })),
      );
    setRemindTarget({ workAreaId: null, areaName: null, staff });
  };

  const closeRemind = () => {
    if (remindMutation.isPending) return; // NHẮC-ING: không cho đóng giữa chừng
    setRemindTarget(null);
    setRemindError(null);
  };

  const sendRemind = () => {
    if (!remindTarget) return;
    setRemindError(null);
    remindMutation.mutate(
      { work_area_id: remindTarget.workAreaId },
      {
        onSuccess: (data) => {
          // NHẮC-DONE: đóng dialog + toast "Đã gửi nhắc tới N nhân viên"
          setRemindTarget(null);
          setToast(`Đã gửi nhắc tới ${data.reminded_count} nhân viên`);
        },
        onError: (error) => {
          // NHẮC-ERROR: banner trong dialog "Gửi nhắc không thành công. Thử lại"
          setRemindError(actionErrorMessage(error, "Gửi nhắc không thành công. Thử lại"));
        },
      },
    );
  };

  // ── Render helpers ──
  const renderSkeletonCard = (rows: number) => (
    <section className={`${styles.card} ${styles.hubCard}`} aria-busy="true">
      <div className={`${styles.skeleton} ${styles.skeletonRow}`} style={{ width: "40%" }} />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`${styles.skeleton} ${styles.skeletonRow}`} />
      ))}
    </section>
  );

  const renderErrorCard = (onRetry: () => void) => (
    <section className={`${styles.card} ${styles.hubCard}`}>
      <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
        Không tải được dữ liệu. Thử lại
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
          style={{ marginLeft: "auto" }}
          onClick={onRetry}
        >
          Thử lại
        </button>
      </div>
    </section>
  );

  const renderAnomaly = (area: CoverageAreaItem) => {
    const parts: React.ReactNode[] = [];
    if (area.missing_clock_in > 0) {
      parts.push(
        <span key="missing" className={styles.covBad}>
          <WarnIcon />
          {area.missing_clock_in} thiếu chấm công
        </span>,
      );
    }
    if (area.late_count > 0) {
      parts.push(
        <span key="late" className={styles.covWarn}>
          {area.late_count} đi trễ
        </span>,
      );
    }
    if (area.gps_deviation_count > 0) {
      parts.push(
        <span key="gps" className={styles.covWarn}>
          {area.gps_deviation_count} GPS lệch
        </span>,
      );
    }
    if (parts.length === 0) return <>—</>;
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && " · "}
            {part}
          </React.Fragment>
        ))}
      </>
    );
  };

  // DD-0103 (ISS-PILOT-17 "thống kê" half): cảnh báo vượt trần OT/ngày (Điều 107)
  // + thiếu nghỉ/tuần (Điều 111), cùng công thức cổng publish — tái dùng đúng
  // pattern covBad/renderAnomaly ở widget [3] Độ phủ phía trên.
  const renderHoursWarning = (emp: EmployeeHoursItem) => {
    const parts: React.ReactNode[] = [];
    if (emp.days_over_ot_cap > 0) {
      parts.push(
        <span key="ot-cap" className={styles.covBad}>
          <WarnIcon />
          {/* #772 (CEO 04/10, UI-UX rule 14): this counts SCHEDULED shifts, not clocked hours. */}
          {emp.days_over_ot_cap} ngày vượt trần OT theo lịch
        </span>,
      );
    }
    if (emp.insufficient_rest_days) {
      parts.push(
        <span key="rest" className={styles.covBad}>
          <WarnIcon />
          Thiếu nghỉ/tuần
        </span>,
      );
    }
    if (parts.length === 0) return <>—</>;
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && " · "}
            {part}
          </React.Fragment>
        ))}
      </>
    );
  };

  // ══════════════════════════════ RENDER ══════════════════════════════
  return (
    <>
      {/* [1] Tiêu đề vùng + ngữ cảnh (đẩy phải) — KHÔNG có nút primary cấp trang */}
      <div className={styles.pageHead}>
        <h1 className={styles.pageHeadTitle}>Tổng quan</h1>
        <span className={styles.hubContext}>
          {branchLabel} · Hôm nay {formatDateVi(new Date())}
        </span>
      </div>

      <div className={styles.hubFlow}>
        {/* ══════ [3] Widget ĐỘ PHỦ THEO KHU VỰC ⓘ · Hôm nay ══════
            (trên mobile lên ĐẦU — ưu tiên xử lý, xem CSS order) */}
        {coverage.isLoading ? (
          renderSkeletonCard(3)
        ) : coverage.isError ? (
          renderErrorCard(() => coverage.refetch())
        ) : coverageData ? (
          <section
            className={`${styles.card} ${styles.hubCard} ${styles.hubCardCoverage}`}
            aria-labelledby="covTitle"
          >
            <div className={styles.hubCardTitle} id="covTitle">
              {/* ISS-MGR-5 (2026-08-09): đổi nhãn "Độ phủ theo khu vực" → "Thống kê theo
                  khu vực" — chỉ đổi label hiển thị, giữ nguyên mọi identifier tiếng Anh
                  (coverage/CoverageTodayResponse/useManagerCoverageToday/hubCardCoverage). */}
              Thống kê theo khu vực
              {/* ⓘ Độ phủ — tooltip microcopy (D10) */}
              <span
                className={`${styles.tooltip} ${styles.infoIco}`}
                tabIndex={0}
                data-tip={COVERAGE_TOOLTIP}
                aria-label={COVERAGE_TOOLTIP}
              >
                <InfoIcon />
              </span>
              <span className={styles.hubCardSub}>· Hôm nay</span>
            </div>

            {coverageData.areas.length === 0 ? (
              <div className={styles.emptyState}>
                Không có ca nào theo lịch hôm nay.
              </div>
            ) : (
              <>
                {/* Bảng (tablet/desktop, cuộn ngang nếu tràn — R5) */}
                <div className={styles.covWrap}>
                  <table className={styles.covTable}>
                    <thead>
                      <tr>
                        <th scope="col">Khu vực</th>
                        <th scope="col">Ca</th>
                        <th scope="col">Đã chấm</th>
                        <th scope="col">Chờ duyệt</th>
                        <th scope="col">Bất thường</th>
                        <th scope="col">Nhắc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverageData.areas.map((area) => (
                        <tr key={area.work_area_id || "unassigned"}>
                          <td>{area.work_area_name}</td>
                          <td>{area.scheduled_shifts}</td>
                          <td>{area.clocked_in}</td>
                          <td>{area.pending_approval}</td>
                          <td>{renderAnomaly(area)}</td>
                          <td>
                            {area.missing_clock_in > 0 ? (
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                                onClick={() => openRemindArea(area)}
                              >
                                Nhắc
                              </button>
                            ) : (
                              <span aria-label="Khu vực đủ phủ">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card con (mobile <768, R5 bảng→card) */}
                <div className={styles.covCards}>
                  {coverageData.areas.map((area) => (
                    <div className={styles.covCard} key={area.work_area_id || "unassigned"}>
                      <div className={styles.covCardName}>{area.work_area_name}</div>
                      <div className={styles.covCardLine}>
                        {area.scheduled_shifts} ca · Đã chấm {area.clocked_in}
                      </div>
                      <div className={styles.covCardLine}>
                        {area.pending_approval} chờ duyệt
                      </div>
                      <div className={styles.covCardLine}>{renderAnomaly(area)}</div>
                      {area.missing_clock_in > 0 && (
                        <div className={styles.covCardLine} style={{ marginTop: "var(--nqh-space-2)" }}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnBlock}`}
                            onClick={() => openRemindArea(area)}
                          >
                            Nhắc
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Dòng tổng + CTA */}
                <div className={styles.covTotal}>
                  Tổng: {coverageData.totals.staff} nhân viên · {coverageData.totals.shifts} ca ·
                  Đã chấm {coverageData.totals.clocked_in} · {coverageData.totals.pending_approval} chờ
                  duyệt ·{" "}
                  {coverageData.totals.missing_clock_in > 0 ? (
                    <strong className={styles.covBad}>
                      {coverageData.totals.missing_clock_in} thiếu chấm công
                    </strong>
                  ) : (
                    <strong>0 thiếu chấm công</strong>
                  )}
                </div>
                <div className={styles.hubCardFoot}>
                  {/* [Mở Chấm công →] → SC-011-ATTEND-MGR (hôm nay) */}
                  <Link className={`${styles.btn} ${styles.btnSecondary}`} to="/manager/attendance">
                    Mở Chấm công →
                  </Link>
                  {/* [Nhắc khu vực thiếu] — nhắc mọi khu vực còn thiếu chấm → dialog [N] tổng hợp */}
                  {coverageData.totals.missing_clock_in > 0 && (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={openRemindAll}
                    >
                      Nhắc khu vực thiếu
                    </button>
                  )}
                </div>
              </>
            )}
          </section>
        ) : null}

        {/* ══════ [2] Widget LỊCH TUẦN (gọn) ══════ */}
        {weekSummary.isLoading ? (
          renderSkeletonCard(2)
        ) : weekSummary.isError ? (
          renderErrorCard(() => weekSummary.refetch())
        ) : weekData ? (
          <section
            className={`${styles.card} ${styles.hubCard} ${styles.hubCardSchedule}`}
            aria-labelledby="weekTitle"
          >
            <div className={styles.hubCardTitle} id="weekTitle">
              Lịch tuần
            </div>
            {weekData.status === "none" ? (
              <div className={styles.emptyState}>Chưa có lịch cho tuần này.</div>
            ) : (
              <div className={styles.weekLine}>
                <strong>
                  Tuần {weekData.week_number} · {formatDayMonth(weekData.week_start)}–
                  {formatDayMonth(weekData.week_end)}
                </strong>
                {/* badge [8]-style: ● Chưa xuất bản / ✔ Đã xuất bản — ISS-MGR-1 REOPEN
                    (2026-08-10, Hang Le): đổi nhãn "Nháp"→"Chưa xuất bản" đồng bộ với
                    ManagerSchedulePage.tsx renderBadge(). Bỏ luôn dòng "chưa xuất bản" mờ
                    riêng bên dưới (đã trở thành trùng lặp với badge — PROPOSAL-SC-009-
                    schedule-audit-panel-2026-08-10.md §3.2 row #18). */}
                {weekData.status === "draft" ? (
                  <span className={`${styles.statusBadge} ${styles.statusBadgeDraft}`}>
                    <span className={styles.statusBadgeDot} aria-hidden="true" />
                    Chưa xuất bản
                  </span>
                ) : (
                  <span className={`${styles.statusBadge} ${styles.statusBadgePublished}`}>
                    <span className={styles.statusBadgeDot} aria-hidden="true" />
                    Đã xuất bản
                  </span>
                )}
                {weekData.warnings_count > 0 && (
                  <span className={styles.weekWarn}>
                    <WarnIcon />
                    {weekData.warnings_count} cảnh báo
                  </span>
                )}
              </div>
            )}
            {/* [6] Dòng nhắc xuất bản — CHỈ Quản lý chi nhánh (biến thể Trưởng ca: hub-shift.html) */}
            {isBranchManager ? (
              <div className={styles.publishHint}>
                {/* BOT-048 (#833): trước đây hard-code "3 ngày" — không đọc
                    config thật. Nay đọc weekData.publish_ahead_days (SSOT
                    per-tenant từ manager_hub.py::get_week_summary()). */}
                <span>Nên xuất bản trước tối thiểu {weekData.publish_ahead_days} ngày</span>
                {/* [Mở Phân ca →] → SC-009-SCHEDULE-GRID */}
                <Link className={`${styles.btn} ${styles.btnSecondary}`} to="/manager/schedule">
                  Mở Phân ca →
                </Link>
              </div>
            ) : (
              <div className={styles.weekCta}>
                <Link className={`${styles.btn} ${styles.btnSecondary}`} to="/manager/schedule">
                  Mở Phân ca →
                </Link>
              </div>
            )}
          </section>
        ) : null}

        {/* ══════ [4] Widget MỚI TỔNG GIỜ LÀM (tuần/tháng, per nhân viên) —
            ISS-MGR-11 / DD-0093. Data contract từ backend đã APPROVED (DD-0093);
            hình dạng UI tái dùng đúng pattern bảng+card responsive của widget
            [3] Thống kê theo khu vực (DD-0093 §4.1 để ngỏ hình dạng cụ thể cho
            @ba/@designer — không có mockup riêng cho widget này). ══════ */}
        {hoursSummary.isLoading ? (
          renderSkeletonCard(3)
        ) : hoursSummary.isError ? (
          renderErrorCard(() => hoursSummary.refetch())
        ) : hoursData ? (
          <section
            className={`${styles.card} ${styles.hubCard} ${styles.hubCardHours}`}
            aria-labelledby="hoursTitle"
          >
            <div className={styles.hubCardTitle} id="hoursTitle">
              Tổng giờ làm
              <span className={styles.hubCardSub}>
                · {formatDayMonth(hoursData.range_start)}–{formatDayMonth(hoursData.range_end)}
              </span>
              <div className={styles.periodToggle} style={{ marginLeft: "auto" }}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSm} ${
                    hoursPeriod === "week" ? styles.btnPrimary : styles.btnSecondary
                  }`}
                  aria-pressed={hoursPeriod === "week"}
                  onClick={() => setHoursPeriod("week")}
                >
                  Tuần
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSm} ${
                    hoursPeriod === "month" ? styles.btnPrimary : styles.btnSecondary
                  }`}
                  aria-pressed={hoursPeriod === "month"}
                  onClick={() => setHoursPeriod("month")}
                >
                  Tháng
                </button>
              </div>
            </div>

            {hoursData.employees.length === 0 ? (
              <div className={styles.emptyState}>
                Không có ca nào theo lịch trong khoảng thời gian này.
              </div>
            ) : (
              <>
                {/* Bảng (tablet/desktop) — cùng pattern .covWrap/.covTable widget [3] */}
                <div className={styles.covWrap}>
                  <table className={styles.covTable}>
                    <thead>
                      <tr>
                        <th scope="col">Nhân viên</th>
                        <th scope="col">Khu vực</th>
                        <th scope="col">Ca</th>
                        <th scope="col">Giờ công</th>
                        <th scope="col">Ngoài giờ</th>
                        <th scope="col">TB/ca</th>
                        <th scope="col">Cảnh báo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hoursData.employees.map((emp) => (
                        <tr key={emp.user_id}>
                          <td>{emp.full_name}</td>
                          <td>{emp.work_area_name}</td>
                          <td>
                            {emp.worked_shifts}/{emp.scheduled_shifts}
                          </td>
                          <td>{emp.total_hours.toFixed(1)}h</td>
                          <td>{emp.overtime_hours > 0 ? `${emp.overtime_hours.toFixed(1)}h` : "—"}</td>
                          <td>{emp.average_hours_per_shift.toFixed(1)}h</td>
                          <td>{renderHoursWarning(emp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card con (mobile <768) — cùng pattern .covCards widget [3] */}
                <div className={styles.covCards}>
                  {hoursData.employees.map((emp) => (
                    <div className={styles.covCard} key={emp.user_id}>
                      <div className={styles.covCardName}>{emp.full_name}</div>
                      <div className={styles.covCardLine}>
                        {emp.work_area_name} · {emp.worked_shifts}/{emp.scheduled_shifts} ca
                      </div>
                      <div className={styles.covCardLine}>
                        {emp.total_hours.toFixed(1)}h công
                        {emp.overtime_hours > 0 && ` · ${emp.overtime_hours.toFixed(1)}h ngoài giờ`}
                      </div>
                      {(emp.days_over_ot_cap > 0 || emp.insufficient_rest_days) && (
                        <div className={styles.covCardLine}>{renderHoursWarning(emp)}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Dòng tổng — cùng pattern .covTotal widget [3] */}
                <div className={styles.covTotal}>
                  Tổng: {hoursData.totals.staff} nhân viên · {hoursData.totals.total_hours.toFixed(1)}h công
                  {hoursData.totals.overtime_hours > 0 &&
                    ` · ${hoursData.totals.overtime_hours.toFixed(1)}h ngoài giờ`}
                  {hoursData.totals.employees_over_ot_cap > 0 &&
                    ` · ${hoursData.totals.employees_over_ot_cap} NV vượt trần OT theo lịch`}
                  {hoursData.totals.employees_insufficient_rest > 0 &&
                    ` · ${hoursData.totals.employees_insufficient_rest} NV thiếu nghỉ/tuần`}
                </div>
              </>
            )}
          </section>
        ) : null}
      </div>

      {/* ══════ [N] DIALOG "Nhắc chấm công" — xác nhận trước khi gửi, giữa màn ~440px ══════
          NHẮC-CONFIRM → [Gửi nhắc] → NHẮC-ING (spinner+disabled) → NHẮC-DONE
          (đóng dialog + toast) · NHẮC-ERROR → banner trong dialog */}
      {remindTarget && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="remindTitle">
            <div className={styles.modalHead}>
              <h2 className={styles.modalTitle} id="remindTitle">
                Gửi nhắc chấm công?
              </h2>
              <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={closeRemind}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.modalBody}>
              {remindError && (
                <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
                  {remindError}
                </div>
              )}
              {remindTarget.workAreaId === null ? (
                <>
                  Gửi thông báo nhắc tới mọi nhân viên CHƯA chấm công hôm nay ở các khu vực
                  thiếu phủ ({remindTarget.staff.length} nhân viên):
                  <ul>
                    {remindTarget.staff.map((s) => (
                      <li key={`${s.user_id}-${s.shift_label}`}>
                        {s.areaName} · {s.full_name} — ca {s.shift_label}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  Gửi thông báo nhắc tới nhân viên khu vực {remindTarget.areaName} CHƯA chấm
                  công hôm nay ({remindTarget.staff.length} nhân viên):
                  <ul>
                    {remindTarget.staff.map((s) => (
                      <li key={`${s.user_id}-${s.shift_label}`}>
                        {s.full_name} — ca {s.shift_label}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className={styles.modalFoot}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={closeRemind}
                disabled={remindMutation.isPending}
              >
                Hủy
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={sendRemind}
                disabled={remindMutation.isPending}
                aria-busy={remindMutation.isPending}
              >
                {remindMutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
                Gửi nhắc
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast (NHẮC-DONE) */}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </>
  );
};

export default ManagerHubPage;
